// src/utils/printReport.js
/* ------------------------------------------------------------------
   Energiberegner – PDF report generator (clean layout + λ + fonts)
   Puts U-værdi section on the FIRST page right after project details.
   Used by <PrintModal/> via downloadPdfReport(options)

   Notes:
   - Supports basePath/assetPrefix so logo + fonts load in dev/prod
   - Registers Unicode fonts to ensure λ renders correctly
------------------------------------------------------------------- */

import React from "react";
import {
    pdf,
    Document,
    Page,
    View,
    Text,
    Image,
    StyleSheet,
    Font,
} from "@react-pdf/renderer";

import formatNumber from "./formatNumber";
import { layerR, calcUValue } from "./uvalueCalculations";
import { computeVarmetabFrame, computeSavingsScenario } from "./reportCalculations";
import { insulationR } from "../components/calculators/uvalue/constructioncard/logic";


/* ───────── helpers & shared constants ───────────────────────── */

const MONTHS_DA = [
    "januar",
    "februar",
    "marts",
    "april",
    "maj",
    "juni",
    "juli",
    "august",
    "september",
    "oktober",
    "november",
    "december",
];

const LAMBDA = "\u03BB"; // λ
const DELTA = "\u0394"; // Δ
const STAR = "\u2605"; // ★
const fmt = (v, d = 2) => formatNumber(v, d);

const TILLAEG_TINY_THRESHOLD = 0.005; // W/m²K
const ALPHA = 0.8; // DS 418 Annex A.3


/* URL helpers --------------------------------------------------- */

function joinUrlParts(...parts) {
    return parts
        .filter(Boolean)
        .map((p, i) => {
            const s = String(p);
            if (i === 0) return s.replace(/\/+$/, "");
            return s.replace(/^\/+/, "").replace(/\/+$/, "");
        })
        .join("/")
        .replace(/\/{2,}/g, "/");
}

/**
 * Build a URL that works in dev & prod.
 * - basePath: Next.js basePath (example: "/energiberegneren")
 * - assetPrefix: optional CDN/asset prefix (example: "https://cdn.example.com")
 */
const withBase = (p = "", opts = {}) => {
    const envBasePath = process.env.NEXT_PUBLIC_BASE_PATH || "";
    const envAssetPrefix = process.env.NEXT_PUBLIC_ASSET_PREFIX || "";

    const basePath = (opts.basePath ?? envBasePath ?? "").trim();
    const assetPrefix = (opts.assetPrefix ?? envAssetPrefix ?? "").trim();

    const path = String(p).replace(/^\/+/, "");
    const urlPath = joinUrlParts(basePath, path);

    if (assetPrefix) return joinUrlParts(assetPrefix, urlPath);
    return `/${urlPath.replace(/^\/+/, "")}`;
};

/**
 * tiny fetch → data-URI helper (avoids CORS issues and relative-URL 404)
 * Returns null on failure.
 */
async function asDataURI(url) {
    try {
        const abs =
            typeof window !== "undefined"
                ? new URL(url, window.location.origin).toString()
                : url;

        const res = await fetch(abs);
        if (!res.ok) return null;
        const blob = await res.blob();
        return await new Promise((ok) => {
            const fr = new FileReader();
            fr.onloadend = () => ok(fr.result);
            fr.readAsDataURL(blob);
        });
    } catch {
        return null;
    }
}

/* Register a Unicode font that includes λ/Δ/★ glyphs (TTF). ------ */

let _fontReady = false;

async function ensurePdfFont(opts = {}) {
    if (_fontReady) return;
    if (typeof window === "undefined") return;

    const reg = async (family, src, weight = "normal") => {
        try {
            Font.register({ family, src, fontWeight: weight });
            return true;
        } catch {
            return false;
        }
    };

    const dejavu = await asDataURI(withBase("fonts/DejaVuSans.ttf", opts));
    const dejavuBold = await asDataURI(withBase("fonts/DejaVuSans-Bold.ttf", opts));
    if (dejavu) {
        await reg("DejaVuSans", dejavu, "normal");
        if (dejavuBold) await reg("DejaVuSans", dejavuBold, "bold");
        _fontReady = true;
        return;
    }

    const noto = await asDataURI(withBase("fonts/NotoSans-Regular.ttf", opts));
    const notoBold = await asDataURI(withBase("fonts/NotoSans-Bold.ttf", opts));
    if (noto) {
        await reg("DejaVuSans", noto, "normal");
        if (notoBold) await reg("DejaVuSans", notoBold, "bold");
        _fontReady = true;
    }
}

/* ---- look-up tables (same JSON as the UI) -------------------- */

import matsRaw from "../data/materials.json";
import indoorsRaw from "../data/indvendigeOvergangsisolans.json";
import outdoorsRaw from "../data/udvendigeOvergangsisolans.json";

const mats = matsRaw.materials ?? matsRaw;
const indoors = indoorsRaw.indvendigeOvergangsisolans ?? indoorsRaw;
const outdoors = outdoorsRaw.udvendigeOvergangsisolans ?? outdoorsRaw;

const matchByValue = (arr, val, tol = 1e-6) =>
    arr.find((o) => Math.abs(o.rValue - val) < tol);

/* ───────── styles ─────────────────────────────────────────── */

const styles = StyleSheet.create({
    page: { padding: 32, fontSize: 10, fontFamily: "DejaVuSans" },

    h1: { fontSize: 20, fontWeight: "bold" },
    h2: { fontSize: 13, fontWeight: "bold", marginTop: 14, marginBottom: 6 },
    p: { fontSize: 11, lineHeight: 1.3, marginBottom: 6 },

    table: { width: "100%", border: "1 solid #ccc", borderCollapse: "collapse" },
    row: { flexDirection: "row" },
    cell: { padding: 4, borderRight: "1 solid #ccc", justifyContent: "center" },

    head: { backgroundColor: "#e8e8e8", fontWeight: "bold" },
    tot1: { backgroundColor: "#f3f3f3", fontWeight: "bold" },
    tot2: { backgroundColor: "#ebebeb", fontWeight: "bold" },

    headerWrap: { marginBottom: 10 },
    headerTop: {
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 8,
    },
    metaCol: { flex: 1, paddingRight: 8 },
    logo: { width: 120, height: 40, objectFit: "contain" },

    coverGrid: { marginTop: 6 },
    kvRow: { flexDirection: "row", marginBottom: 4 },
    kvKey: { width: 120, color: "#555" },
    kvVal: { flex: 1, fontSize: 12 },

    hr: { height: 1, backgroundColor: "#ddd", marginTop: 10, marginBottom: 10 },

    allowanceBox: {
        marginTop: 6,
        padding: 6,
        border: "1 solid #ddd",
        backgroundColor: "#fafafa",
    },
    allowanceTitle: { fontSize: 10, fontWeight: "bold", marginBottom: 2 },
    allowanceLine: { fontSize: 10, lineHeight: 1.25 },

    footer: {
        position: "absolute",
        bottom: 18,
        left: 32,
        right: 32,
        fontSize: 9,
        color: "#666",
        flexDirection: "row",
        justifyContent: "space-between",
    },
});

/* ───────── shared formatting helpers ───────────────────────── */

function formatDelta(delta, decimals = 3) {
    if (!Number.isFinite(Number(delta))) return "—";
    const d = Number(delta);

    const abs = Math.abs(d);
    if (abs > 0 && abs < TILLAEG_TINY_THRESHOLD) {
        return `< ${fmt(TILLAEG_TINY_THRESHOLD, 3)} W/m²K`;
    }
    return `${fmt(d, decimals)} W/m²K`;
}
function calcDeltaUb(lambda, n, d_mm, d1_m, R1, RT) {
    if (!lambda || !d1_m || !n || !d_mm || !R1 || !RT) return 0;
    const Ab = Math.PI * (d_mm ** 2) / 4e6; // mm² → m²
    return (ALPHA * lambda * Ab * n / d1_m) * ((R1 / RT) ** 2);
}

function formatLambdaMaybe(v) {
    if (v == null) return "—";
    const n = Number(v);
    if (Number.isFinite(n)) return fmt(n, 0);
    return String(v);
}

function formatLenMaybe(v) {
    if (v == null || v === "") return "—";
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    return `${fmt(n, 2)} m`;
}

function buildAllowanceLines(card) {
    const a = card?.allow || {};
    const lines = [];

    if (a?.addA2) {
        lines.push(`A.2: ${DELTA}U${STAR} = ${formatDelta(card?.deltaA2, 3)}`);
    }

    if (a?.addA3) {
        const n = a.nFast ?? "—";
        const d = a.dia ? `Ø${a.dia} mm` : "Ø—";
        const lambda = formatLambdaMaybe(a.lambdaFast ?? a.lambdaFastMode);
        const L = formatLenMaybe(a.len);

        lines.push(
            `A.3: ${DELTA}U = ${formatDelta(card?.deltaA3, 3)} (${n}× ${d}, ${LAMBDA} ${lambda}, d1 ${L})`
        );
    }

    return lines;
}

function layerLabel(l) {
    if (l.customName?.trim()) return l.customName.trim();
    if (l.itemName?.trim()) return l.itemName.trim();

    if (l.catIndex != null && l.itemIndex != null) {
        const cat = mats[l.catIndex];
        const item = cat?.items?.[l.itemIndex];
        if (item?.name) return item.name;
    }
    return l.name || l.label || l.title || "Lag";
}

function transitionDesc(kind, card) {
    if (kind === "rsi") {
        if (card.RsiDesc || card.RsiLabel) return card.RsiDesc || card.RsiLabel;
        if (card.RsiIdx != null) return indoors[card.RsiIdx]?.description;
        return matchByValue(indoors, card.RsiVal)?.description || "Indvendig overgang";
    }
    if (card.RseDesc || card.RseLabel) return card.RseDesc || card.RseLabel;
    if (card.RseIdx != null) return outdoors[card.RseIdx]?.description;
    return matchByValue(outdoors, card.RseVal)?.description || "Udvendig overgang";
}

// replace the whole enrichCardForReport(card) with this
function enrichCardForReport(card) {
    const toNum = (v) => {
        if (v === null || v === undefined || v === "") return NaN;
        if (typeof v === "number") return v;
        // handle Danish decimals: "0,09" -> "0.09"
        const s = String(v).trim().replace(",", ".");
        const n = Number(s);
        return Number.isFinite(n) ? n : NaN;
    };

    const pickNum = (...vals) => {
        for (const v of vals) {
            const n = toNum(v);
            if (Number.isFinite(n)) return n;
        }
        return NaN;
    };

    const Rsi = pickNum(card?.RsiVal) || 0;
    const Rse = pickNum(card?.RseVal) || 0;
    const layers = Array.isArray(card.layers) ? card.layers : [];

    const rLayers = layers.reduce((s, l) => s + (layerR(l) || 0), 0);
    const totalR = (rLayers + Rsi + Rse) || 0;

    const totalThickness_m = layers.reduce(
        (s, l) => s + (pickNum(l?.thickness_m) || 0),
        0
    );

    const allow = card?.allow || {};

    // Prefer card-provided RT/Ri/R1 if they exist (matches the UI better),
    // otherwise fall back to computed values.
    const RT_allow = pickNum(card?.RT, card?.Rt, card?.rt, totalR) || totalR;

    const Ri_fallback = layers.reduce((s, l) => {
        // insulationR is already in your codebase; use same logic as the UI
        const r = insulationR(l, mats);
        return s + (Number.isFinite(r) ? r : 0);
    }, 0);

    const Ri = pickNum(card?.Ri, card?.RI, card?.ri, allow?.Ri, Ri_fallback) || 0;

    // R1: manual/custom override in allow first, then card.R1 (UI auto), then ids, then Ri
    const ids = Array.isArray(allow?.r1LayerIds) ? allow.r1LayerIds : [];
    const R1_fromIds =
        allow?.r1Mode === "manual" && ids.length
            ? layers.reduce((s, l) => (ids.includes(l.id) ? s + (insulationR(l, mats) || 0) : s), 0)
            : Ri;

    const R1 = pickNum(allow?.R1, card?.R1, R1_fromIds, Ri) || 0;

    // A2 delta like UI
    const Ustar = pickNum(allow?.Ustar, allow?.ustar) || 0;
    const ratio = RT_allow ? (Ri / RT_allow) : 0;
    const deltaA2 =
        allow?.addA2 && Ustar > 0 && RT_allow > 0
            ? Ustar * (ratio ** 2)
            : 0;

    // A3 delta like UI (robust numeric parsing)
    const lambdaEff =
        allow?.lambdaFastMode === "custom"
            ? pickNum(allow?.lambdaFast)
            : pickNum(allow?.lambdaFastMode, allow?.lambdaFast);

    const nFast = pickNum(allow?.nFast);
    const dia = pickNum(allow?.dia);
    const d1 = pickNum(allow?.len);

    const deltaA3 =
        allow?.addA3
            ? calcDeltaUb(
                Number.isFinite(lambdaEff) ? lambdaEff : 0,
                Number.isFinite(nFast) ? nFast : 0,
                Number.isFinite(dia) ? dia : 0,
                Number.isFinite(d1) ? d1 : 0,
                R1,
                RT_allow
            )
            : 0;

    // Feed calcUValue with Ri + an R1 value even in auto mode
    const totalU = calcUValue(layers, { Rsi, Rse, ...allow, Ri, R1 });

    return {
        ...card,
        RsiVal: Rsi,
        RseVal: Rse,
        totalR,
        totalU,
        totalThickness_m,
        deltaA2,
        deltaA3,
    };
}


/* ───────── header block (flows at top of each page) ───────── */

function HeaderBlock({ projectName, author, reportNote, dateStr, logoData }) {
    return (
        <View style={styles.headerWrap}>
            <View style={styles.headerTop}>
                <View style={styles.metaCol}>
                    <Text style={styles.h1}>Energiberegner – Rapport</Text>
                    <View style={styles.coverGrid}>
                        <View style={styles.kvRow}>
                            <Text style={styles.kvKey}>Projekt</Text>
                            <Text style={styles.kvVal}>{projectName || "—"}</Text>
                        </View>
                        <View style={styles.kvRow}>
                            <Text style={styles.kvKey}>Udarbejdet af</Text>
                            <Text style={styles.kvVal}>{author || "—"}</Text>
                        </View>
                        <View style={styles.kvRow}>
                            <Text style={styles.kvKey}>Dato</Text>
                            <Text style={styles.kvVal}>{dateStr}</Text>
                        </View>
                    </View>
                </View>

                {!!logoData && <Image src={logoData} style={styles.logo} />}
            </View>

            {!!reportNote && (
                <>
                    <View style={styles.hr} />
                    <Text style={styles.p}>{reportNote}</Text>
                </>
            )}

            <View style={styles.hr} />
        </View>
    );
}

function Footer({ projectName }) {
    return (
        <View style={styles.footer} fixed>
            <Text>{projectName ? String(projectName) : "Energiberegner"}</Text>
            <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
    );
}

/* ───────── section renderers ───────────────────────── */

function renderConstruction(card, idx) {
    const rows = [];

    // Rsi
    rows.push(["Rsi", transitionDesc("rsi", card), "—", fmt(card.RsiVal, 3)]);

    // Layers
    (card.layers || []).forEach((l, i) => {
        let desc = layerLabel(l);

        if (l.type === "homogeneous") {
            if (l.kind === "lambda") desc += ` (${LAMBDA} ${fmt(l.lambda, 3)})`;
            else desc += ` (R ${fmt(l.rValue, 3)})`;
        } else if (l.type === "composite") {
            if (l.lambdaIns != null) desc += ` (${LAMBDA} ${fmt(l.lambdaIns, 3)})`;
            else {
                const R = layerR(l);
                if (l.thickness_m && R) desc += ` (${LAMBDA} ${fmt(l.thickness_m / R, 3)})`;
            }
        } else if (l.type === "customMaterial") {
            if (l.mode === "lambda") desc += ` (${LAMBDA} ${fmt(l.customLambda, 3)})`;
            else desc += ` (R ${fmt(l.customR, 3)})`;
        }

        rows.push([
            i + 1,
            desc,
            l.thickness_m ? fmt(l.thickness_m, 3) : "—",
            fmt(layerR(l), 3),
        ]);
    });

    // Rse
    rows.push(["Rse", transitionDesc("rse", card), "—", fmt(card.RseVal, 3)]);

    // Total isolans (no redundant unit suffix)
    rows.push(["", "Total isolans", "—", fmt(card.totalR, 3)]);

    // U row (as requested):
    // - "U-værdi" in # column
    // - Value in "Materiale / lag" column
    // - Thickness total stays in thickness column
    // - Isolans column left blank
    rows.push([
        "U-værdi",
        `${fmt(card.totalU, 2)} W/m²K`,
        fmt(card.totalThickness_m, 3),
        "",
    ]);

    const note = (card.note || "").trim();
    const allowLines = buildAllowanceLines(card);

    return (
        <View
            key={card.id || idx}
            wrap={false}
            minPresenceAhead={140}
            style={{ marginBottom: 12 }}
        >
            <Text style={{ fontSize: 11, fontWeight: "bold", marginBottom: 2 }}>
                {idx}. {card.title || "Konstruktion"}
            </Text>

            {note && <Text style={{ fontSize: 10, marginBottom: 4 }}>{note}</Text>}

            <View style={styles.table}>
                <View style={[styles.row, styles.head]}>
                    {["#", "Materiale / lag", "Tykkelse [m]", "Isolans [m²K/W]"].map((h, i) => (
                        <Text key={h} style={[styles.cell, { flex: i === 1 ? 3 : 1 }]}>
                            {h}
                        </Text>
                    ))}
                </View>

                {rows.map((r, i) => {
                    const isTotalIsolans = String(r?.[1] ?? "") === "Total isolans";
                    const isU = String(r?.[0] ?? "") === "U-værdi";

                    const extra = isTotalIsolans ? styles.tot1 : isU ? styles.tot2 : null;

                    // This is your “small gap” between Total isolans and U row.
                    // React-PDF tolerates marginTop here well enough for a subtle separation.
                    const gap = isU ? { marginTop: 4 } : null;

                    return (
                        <View key={i} style={[styles.row, extra, gap]}>
                            {r.map((c, j) => (
                                <Text key={j} style={[styles.cell, { flex: j === 1 ? 3 : 1 }]}>
                                    {c}
                                </Text>
                            ))}
                        </View>
                    );
                })}
            </View>

            {allowLines.length > 0 && (
                <View style={styles.allowanceBox}>
                    <Text style={styles.allowanceTitle}>Tillæg</Text>
                    {allowLines.map((t, i) => (
                        <Text key={i} style={styles.allowanceLine}>
                            {t}
                        </Text>
                    ))}
                </View>
            )}
        </View>
    );
}

function UvalueSection({ deck = [] }) {
    if (!deck.length) return null;
    const enriched = deck.map(enrichCardForReport);

    return (
        <View>
            <Text style={styles.h2}>U-værdi-beregner</Text>
            {enriched.map((c, i) => renderConstruction(c, i + 1))}
        </View>
    );
}

const BTYPE_LABEL = { helårs: "Helårshus", sommer: "Sommerhus" };
const PTYPE_LABEL = { tilbygning: "Tilbygning", renovering: "Renovering" };

function KV({ k, v }) {
    return (
        <View style={styles.kvRow}>
            <Text style={styles.kvKey}>{k}</Text>
            <Text style={[styles.kvVal, { fontSize: 10 }]}>{v}</Text>
        </View>
    );
}

function VarmetabSection({ data }) {
    if (!data) return null;

    const r = computeVarmetabFrame(data);
    const isTilbygning = r.projectType === "tilbygning";

    const cols = isTilbygning
        ? [
            ["Design [W]", fmt(r.design, 1)],
            ["Reference [W]", fmt(r.reference, 1)],
            ["Afvigelse [W]", `${r.diff >= 0 ? "+" : ""}${fmt(r.diff, 1)}`],
            ["Afvigelse [%]", `${r.pct >= 0 ? "+" : ""}${fmt(r.pct, 1)}`],
        ]
        : [["Design [W]", fmt(r.design, 1)]];

    return (
        <View>
            <Text style={styles.h2}>Varmetabs-ramme</Text>

            <View style={{ marginBottom: 6 }}>
                <KV k="Bygningstype" v={BTYPE_LABEL[r.bType] || r.bType} />
                <KV k="Projekttype" v={PTYPE_LABEL[r.projectType] || String(r.projectType)} />
                <KV k="Etageareal" v={r.floorArea != null ? `${fmt(r.floorArea, 1)} m²` : "—"} />
            </View>

            {!r.hasData ? (
                <Text style={styles.p}>Der er ikke gemt nogen varmetabsberegning for projektet.</Text>
            ) : (
                <>
                    <View style={styles.table}>
                        <View style={[styles.row, styles.head]}>
                            {cols.map(([h]) => (
                                <Text key={h} style={[styles.cell, { flex: 1 }]}>
                                    {h}
                                </Text>
                            ))}
                        </View>
                        <View style={styles.row}>
                            {cols.map(([h, v]) => (
                                <Text key={h} style={[styles.cell, { flex: 1 }]}>
                                    {v}
                                </Text>
                            ))}
                        </View>
                    </View>

                    {isTilbygning && (
                        <>
                            <Text style={[styles.p, { marginTop: 6, fontWeight: "bold" }]}>
                                {r.ok
                                    ? "Designet opfylder referencerammen (tilbygning)."
                                    : "Designet overskrider referencerammen (tilbygning)."}
                            </Text>

                            <View style={styles.allowanceBox}>
                                <Text style={styles.allowanceTitle}>Referenceramme</Text>
                                <Text style={styles.allowanceLine}>
                                    Fradrag (50 %) for dækkede konstruktioner: {fmt(r.deduction, 1)} W
                                </Text>
                                <Text style={styles.allowanceLine}>
                                    Glasareal: {fmt(r.glassArea, 2)} m²
                                    {r.glassFrac != null ? ` (${fmt(r.glassFrac, 1)} % af etagearealet)` : ""}
                                    {r.glassLimit != null ? ` • grænse ${fmt(r.glassLimit, 2)} m²` : ""}
                                </Text>
                                {r.extraGlass > 0 && (
                                    <Text style={styles.allowanceLine}>
                                        Overskydende glas regnet som ydervæg: {fmt(r.extraGlass, 2)} m² ({fmt(r.glassAdjust, 1)} W)
                                    </Text>
                                )}
                            </View>
                        </>
                    )}
                </>
            )}

            {data?.info?.note && (
                <Text style={{ marginTop: 4 }}>{String(data.info.note)}</Text>
            )}
        </View>
    );
}

function SavingsSection({ data }) {
    if (!data) return null;

    const list = (Array.isArray(data) ? data : [data]).map(computeSavingsScenario);

    return (
        <View>
            <Text style={styles.h2}>Årlig besparelse</Text>

            <View style={styles.table}>
                <View style={[styles.row, styles.head]}>
                    {["Scenario", "Energibesparelse [kWh]", "Besparelse [kr]", "Brændsel"].map(
                        (h) => (
                            <Text
                                key={h}
                                style={[styles.cell, { flex: h === "Scenario" ? 2 : 1 }]}
                            >
                                {h}
                            </Text>
                        )
                    )}
                </View>

                {list.map((s, i) => (
                    <View key={i} style={styles.row}>
                        <View style={[styles.cell, { flex: 2 }]}>
                            <Text>{s.name}</Text>
                            {!!(s.fuelName || s.heatingName) && (
                                <Text style={{ fontSize: 8, color: "#666" }}>
                                    {[s.fuelName, s.heatingName].filter(Boolean).join(" • ")}
                                </Text>
                            )}
                        </View>
                        <Text style={[styles.cell, { flex: 1 }]}>{fmt(s.kWhSaved, 1)}</Text>
                        <Text style={[styles.cell, { flex: 1 }]}>{fmt(s.annualCost, 1)}</Text>
                        <Text style={[styles.cell, { flex: 1 }]}>
                            {s.unit ? `${fmt(s.annualUnits, 1)} ${s.unit}` : fmt(s.annualUnits, 1)}
                        </Text>
                    </View>
                ))}
            </View>
        </View>
    );
}

/* ───────── exported builder & downloader ───────── */

export async function downloadPdfReport({
    projectName = "",
    uvalueData = [],
    varmetabData = null,
    savingsData = null,
    author = "",
    reportNote = "",
    basePath = undefined,
    assetPrefix = undefined,
} = {}) {
    const urlOpts = { basePath, assetPrefix };
    await ensurePdfFont(urlOpts);

    const now = new Date();
    const dateStr = `${now.getDate()}. ${MONTHS_DA[now.getMonth()]} ${now.getFullYear()}`;

    const logoUri =
        (await asDataURI(withBase("images/system/isoverlogo.png", urlOpts))) ||
        (await asDataURI(withBase("images/isoverlogo.png", urlOpts))) ||
        null;

    const Doc = (
        <Document>
            <Page size="A4" style={styles.page}>
                <HeaderBlock
                    projectName={projectName}
                    author={author}
                    reportNote={reportNote}
                    dateStr={dateStr}
                    logoData={logoUri}
                />
                <UvalueSection deck={uvalueData} />
                <Footer projectName={projectName} />
            </Page>

            {varmetabData && (
                <Page size="A4" style={styles.page}>
                    <HeaderBlock
                        projectName={projectName}
                        author={author}
                        reportNote={""}
                        dateStr={dateStr}
                        logoData={logoUri}
                    />
                    <VarmetabSection data={varmetabData} />
                    <Footer projectName={projectName} />
                </Page>
            )}

            {savingsData && (
                <Page size="A4" style={styles.page}>
                    <HeaderBlock
                        projectName={projectName}
                        author={author}
                        reportNote={""}
                        dateStr={dateStr}
                        logoData={logoUri}
                    />
                    <SavingsSection data={savingsData} />
                    <Footer projectName={projectName} />
                </Page>
            )}
        </Document>
    );

    const blob = await pdf(Doc).toBlob();
    const fname = `${(projectName || "Energiberegner")
        .replace(/[\\/:*?"<>|]+/g, "_")} - ${now.toISOString().slice(0, 10)}.pdf`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

export async function buildPdfBlob(opts = {}) {
    const urlOpts = { basePath: opts.basePath, assetPrefix: opts.assetPrefix };
    await ensurePdfFont(urlOpts);

    const now = new Date();
    const dateStr = `${now.getDate()}. ${MONTHS_DA[now.getMonth()]} ${now.getFullYear()}`;

    const logoUri =
        (await asDataURI(withBase("images/system/isoverlogo.png", urlOpts))) ||
        (await asDataURI(withBase("images/isoverlogo.png", urlOpts))) ||
        null;

    const Doc = (
        <Document>
            <Page size="A4" style={styles.page}>
                <HeaderBlock
                    projectName={opts.projectName || ""}
                    author={opts.author || ""}
                    reportNote={opts.reportNote || ""}
                    dateStr={dateStr}
                    logoData={logoUri}
                />
                <UvalueSection deck={opts.uvalueData || []} />
                <Footer projectName={opts.projectName} />
            </Page>

            {opts.varmetabData && (
                <Page size="A4" style={styles.page}>
                    <HeaderBlock
                        projectName={opts.projectName || ""}
                        author={opts.author || ""}
                        reportNote={""}
                        dateStr={dateStr}
                        logoData={logoUri}
                    />
                    <VarmetabSection data={opts.varmetabData} />
                    <Footer projectName={opts.projectName} />
                </Page>
            )}

            {opts.savingsData && (
                <Page size="A4" style={styles.page}>
                    <HeaderBlock
                        projectName={opts.projectName || ""}
                        author={opts.author || ""}
                        reportNote={""}
                        dateStr={dateStr}
                        logoData={logoUri}
                    />
                    <SavingsSection data={opts.savingsData} />
                    <Footer projectName={opts.projectName} />
                </Page>
            )}
        </Document>
    );

    return pdf(Doc).toBlob();
}
